import * as Sentry from '@sentry/browser';
import { GraphQLError } from 'graphql';
import { v4 as uuidv4 } from 'uuid';

import Toaster from '../components/Toaster';
import { useSessionContext } from '../contexts/SessionContext';
import {
  OdProtosCabinetData_Asset_Type,
  OdProtosRefurbyData_MessageThreadStatus,
} from '../graphql/athena/types/globalTypes';
import { useBulkCreateCabinetAssets } from '../graphql/mutations/cabinet/useBulkCreateCabinetAssets';
import { usePostToWorkOrderMessageThread } from '../graphql/mutations/refurby/usePostToWorkOrderMessageThread';
import { usePostToWorkOrderOnSiteMessageThread } from '../graphql/mutations/refurby/usePostToWorkOrderOnSiteMessageThread';
import { useBulkUploadPhotosToS3 } from '../hooks/useBulkUploadPhotosToS3';

export const usePostNotesAndPhotos = () => {
  const { session } = useSessionContext();
  const [postToWorkOrderMessageThread] = usePostToWorkOrderMessageThread();
  const [postToWorkOrderOnSiteMessageThread] = usePostToWorkOrderOnSiteMessageThread();
  const [bulkCreateCabinetAssets] = useBulkCreateCabinetAssets();
  const [bulkUploadPhotosToS3] = useBulkUploadPhotosToS3();

  return [
    async (workOrderId: string, message: string, files: File[], isOnsite: boolean) => {
      const postNotesParams = {
        variables: {
          workOrderId,
          content: message,
          newStatus: isOnsite
            ? OdProtosRefurbyData_MessageThreadStatus.NO_ACTION_REQUIRED
            : OdProtosRefurbyData_MessageThreadStatus.WAITING_ON_OPENDOOR_RESPONSE,
          authorId: session?.human?.token ?? '',
        },
      };
      let notesId = '',
        notesErrors: readonly GraphQLError[] | undefined;

      if (isOnsite) {
        const { data: messageThreadResponse, errors } = await postToWorkOrderOnSiteMessageThread(
          postNotesParams,
        );
        notesId =
          messageThreadResponse?.refurby?.postToWorkOrderOnSiteMessageThread?.onSiteMessageThread
            ?.notes?.[0]?.id ?? '';
        notesErrors = errors;
      } else {
        const { data: messageThreadResponse, errors } = await postToWorkOrderMessageThread(
          postNotesParams,
        );
        notesId =
          messageThreadResponse?.refurby?.postToWorkOrderMessageThread?.messageThread?.notes?.[0]
            ?.id ?? '';
        notesErrors = errors;
      }

      if (notesId === '' || notesErrors) {
        Sentry.withScope((scope) => {
          scope.setTag('source', 'WorkOrderProgress');
          scope.setExtras({ workOrderId, isOnsiteNotes: isOnsite });
          scope.setExtras({ notesErrors: notesErrors });
          Sentry.captureException(new Error('Failed to create notes'));
        });
        Toaster.show({
          message: `Failed to create notes. Please try again after sometime.`,
          intent: 'danger',
        });
        return false;
      }

      if (files.length > 0) {
        const paths = await bulkUploadPhotosToS3(files);
        await bulkCreateCabinetAssets({
          variables: {
            assets: paths.map((path) => ({
              id: uuidv4(),
              fullPath: path,
              source: 'TradePartnerPortal',
              assetType: OdProtosCabinetData_Asset_Type.IMAGE,
              attachTo: [
                {
                  major: notesId,
                  minor: isOnsite
                    ? 'ProgressVisitsOnsiteMessageThread'
                    : 'ProgressVisitsMessageThread',
                  source: 'TradePartnerPortal',
                },
              ],
            })),
          },
        }).catch((e) => {
          Sentry.withScope((scope) => {
            scope.setTag('source', 'WorkOrderProgress');
            scope.setExtras({
              workOrderId,
              notesId: notesId,
              isOnsiteNotes: isOnsite,
            });
            scope.setExtras({ bulkCreateCabinetAssetsError: e });
            Sentry.captureException(new Error('Failed to create assets for notes'));
          });
          Toaster.show({
            message: `Failed to create assets. Please upload again.`,
            intent: 'danger',
          });
          return false;
        });
      }
      Toaster.show({
        message: `Notes created successfully.`,
        intent: 'success',
      });
      return true;
    },
  ];
};
